import { supabase } from './client';
import { Permission, UserEmpresa, AuditLog, OperadorInfo, UserRole } from '@/types/permissions';

// ==================== USUÁRIOS E PERMISSÕES ====================

export async function getUsersFromEmpresa(empresaId: string): Promise<OperadorInfo[]> {
  const { data: usersData, error } = await supabase
    .from('user_empresas')
    .select(
      `
      id,
      user_id,
      role,
      is_owner,
      status,
      created_at
      `
    )
    .eq('empresa_id', empresaId);

  if (error) throw error;

  // Buscar nomes dos usuários em auth.users via profiles
  const userIds = usersData?.map((u) => u.user_id) || [];
  const { data: profiles } = await supabase
    .from('profiles')
    .select('user_id, email, full_name')
    .in('user_id', userIds);

  const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) || []);

  return (usersData || []).map((user) => {
    const profile = profileMap.get(user.user_id);
    return {
      id: user.id,
      email: profile?.email || '',
      full_name: profile?.full_name || 'Usuário',
      role: user.role,
      status: user.status,
      is_owner: user.is_owner,
      created_at: user.created_at
    };
  });
}

export async function createOperador(
  empresaId: string,
  userId: string,
  role: UserRole = 'operator'
): Promise<UserEmpresa> {
  const { data, error } = await supabase
    .from('user_empresas')
    .insert({
      user_id: userId,
      empresa_id: empresaId,
      role,
      is_owner: false,
      status: 'ativo'
    })
    .select()
    .single();

  if (error) throw error;

  // Criar permissões padrão chamando a função Supabase
  await supabase.rpc('create_default_permissions', {
    p_user_id: userId,
    p_empresa_id: empresaId,
    p_role: role
  });

  return data;
}

export async function updateOperadorStatus(
  userEmpresaId: string,
  status: 'ativo' | 'inativo'
): Promise<void> {
  const { error } = await supabase
    .from('user_empresas')
    .update({ status })
    .eq('id', userEmpresaId);

  if (error) throw error;
}

export async function deleteOperador(userEmpresaId: string): Promise<void> {
  const { error } = await supabase.from('user_empresas').delete().eq('id', userEmpresaId);

  if (error) throw error;
}

// ==================== PERMISSÕES ====================

export async function getUserPermissions(
  userId: string,
  empresaId: string
): Promise<Record<string, Record<string, boolean>>> {
  const { data, error } = await supabase
    .from('user_permissions')
    .select('module, actions')
    .eq('user_id', userId)
    .eq('empresa_id', empresaId);

  if (error) throw error;

  const permissions: Record<string, Record<string, boolean>> = {};
  data?.forEach((perm) => {
    permissions[perm.module] = perm.actions || {};
  });

  return permissions;
}

export async function updateModulePermissions(
  userId: string,
  empresaId: string,
  module: string,
  actions: Record<string, boolean>
): Promise<void> {
  const { error } = await supabase
    .from('user_permissions')
    .upsert(
      {
        user_id: userId,
        empresa_id: empresaId,
        module,
        actions
      },
      {
        onConflict: 'user_id,empresa_id,module'
      }
    );

  if (error) throw error;
}

export async function hasPermission(
  userId: string,
  empresaId: string,
  module: string,
  action: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('has_permission', {
      p_user_id: userId,
      p_empresa_id: empresaId,
      p_module: module,
      p_action: action
    });

    if (error) throw error;
    return data || false;
  } catch {
    return false;
  }
}

// ==================== AUDITORIA ====================

export async function logAuditAction(
  empresaId: string,
  action: string,
  module: string,
  entityType?: string,
  entityId?: string,
  changes?: Record<string, any>
): Promise<void> {
  await supabase.rpc('log_audit_action', {
    p_empresa_id: empresaId,
    p_action: action,
    p_module: module,
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_changes: changes
  });
}

export async function getAuditLogs(
  empresaId: string,
  limit: number = 100,
  offset: number = 0
): Promise<AuditLog[]> {
  const { data, error } = await supabase
    .from('audit_logs')
    .select(
      `
      id,
      empresa_id,
      user_id,
      action,
      module,
      entity_type,
      entity_id,
      changes,
      ip_address,
      user_agent,
      created_at
      `
    )
    .eq('empresa_id', empresaId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data || [];
}

export async function getAuditLogsByEntity(
  empresaId: string,
  entityType: string,
  entityId: string
): Promise<AuditLog[]> {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}
